'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSearch,
    MdClose,
    MdReceipt,
    MdRefresh,
    MdFilterList,
    MdVisibility,
    MdQrCode,
    MdSend,
    MdCancel,
    MdPerson,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getElectronicPrescriptions,
    sendElectronicPrescription,
    cancelElectronicPrescription,
    getElectronicPrescriptionQrCode,
    ElectronicPrescription,
    ElectronicPrescriptionStatus,
    PRESCRIPTION_STATUS_LABELS,
    PRESCRIPTION_STATUS_COLORS,
} from '@/lib/api/health-integrations/pharmacy-api'

export default function ElectronicPrescriptionsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()

    const [prescriptions, setPrescriptions] = useState<
        ElectronicPrescription[]
    >([])
    const [filteredPrescriptions, setFilteredPrescriptions] = useState<
        ElectronicPrescription[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<
        ElectronicPrescriptionStatus | ''
    >('')
    const [selectedPrescription, setSelectedPrescription] =
        useState<ElectronicPrescription | null>(null)
    const [qrCodeModal, setQrCodeModal] = useState<{
        open: boolean
        qrCode: string
    }>({ open: false, qrCode: '' })
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [prescriptionToCancel, setPrescriptionToCancel] = useState<
        number | null
    >(null)
    const [cancelling, setCancelling] = useState(false)
    const [sending, setSending] = useState<number | null>(null)

    useEffect(() => {
        if (user?.companyId) {
            loadPrescriptions()
        }
    }, [user?.companyId, statusFilter])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = prescriptions.filter(
                prescription =>
                    prescription.client?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    prescription.professional?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    prescription.accessCode
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredPrescriptions(filtered)
        } else {
            setFilteredPrescriptions(prescriptions)
        }
    }, [searchTerm, prescriptions])

    const loadPrescriptions = async () => {
        setLoading(true)
        try {
            const filters: any = {}
            if (statusFilter) filters.status = statusFilter

            const response = await getElectronicPrescriptions(filters)
            if (response.success) {
                setPrescriptions(response.result)
                setFilteredPrescriptions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar receitas')
            }
        } catch (error) {
            toast.error('Erro ao carregar receitas')
        } finally {
            setLoading(false)
        }
    }

    const handleSend = async (id: number) => {
        setSending(id)
        try {
            const response = await sendElectronicPrescription(id)
            if (response.success) {
                toast.success('Receita enviada com sucesso')
                loadPrescriptions()
            } else {
                toast.error(response.error || 'Erro ao enviar receita')
            }
        } catch (error) {
            toast.error('Erro ao enviar receita')
        } finally {
            setSending(null)
        }
    }

    const handleShowQrCode = async (id: number) => {
        try {
            const response = await getElectronicPrescriptionQrCode(id)
            if (response.success) {
                setQrCodeModal({ open: true, qrCode: response.result.qrCode })
            } else {
                toast.error(response.error || 'Erro ao carregar QR Code')
            }
        } catch (error) {
            toast.error('Erro ao carregar QR Code')
        }
    }

    const handleCancel = (id: number) => {
        setPrescriptionToCancel(id)
        setShowCancelModal(true)
    }

    const confirmCancel = async () => {
        if (!prescriptionToCancel) return

        setCancelling(true)
        try {
            const response =
                await cancelElectronicPrescription(prescriptionToCancel)
            if (response.success) {
                toast.success('Receita cancelada')
                loadPrescriptions()
                setShowCancelModal(false)
                setPrescriptionToCancel(null)
            } else {
                toast.error(response.error || 'Erro ao cancelar receita')
            }
        } catch (error) {
            toast.error('Erro ao cancelar receita')
        } finally {
            setCancelling(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando receitas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() =>
                            navigate('/health-integrations/pharmacy')
                        }
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <MdReceipt className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                Receitas Eletrônicas
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {prescriptions.length}{' '}
                                {prescriptions.length === 1
                                    ? 'receita'
                                    : 'receitas'}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={loadPrescriptions}
                    className="btn btn-ghost btn-sm gap-2"
                >
                    <MdRefresh className="text-xl" />
                    Atualizar
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente, profissional ou código..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-xl text-base-content/60" />
                        <select
                            className="select select-bordered"
                            value={statusFilter}
                            onChange={e =>
                                setStatusFilter(
                                    e.target.value as
                                        | ElectronicPrescriptionStatus
                                        | ''
                                )
                            }
                        >
                            <option value="">Todos os status</option>
                            {Object.entries(PRESCRIPTION_STATUS_LABELS).map(
                                ([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredPrescriptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdReceipt className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma receita encontrada
                        </h3>
                        <p className="text-sm text-base-content/60">
                            {searchTerm || statusFilter
                                ? 'Tente ajustar seus filtros'
                                : 'Receitas eletrônicas aparecerão aqui'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Paciente</th>
                                    <th>Profissional</th>
                                    <th>Integração</th>
                                    <th>Status</th>
                                    <th>Validade</th>
                                    <th>Data</th>
                                    <th>Acoes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPrescriptions.map(prescription => (
                                    <tr key={prescription.id} className="hover">
                                        <td>
                                            {prescription.client ? (
                                                <div className="flex items-center gap-2">
                                                    <MdPerson className="text-lg text-base-content/60" />
                                                    <span>
                                                        {
                                                            prescription.client
                                                                .name
                                                        }
                                                    </span>
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>
                                            {prescription.professional?.name ||
                                                '-'}
                                        </td>
                                        <td>
                                            {prescription.integration?.name ||
                                                '-'}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${PRESCRIPTION_STATUS_COLORS[prescription.status]}`}
                                            >
                                                {
                                                    PRESCRIPTION_STATUS_LABELS[
                                                        prescription.status
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            {prescription.validUntil
                                                ? new Date(
                                                      prescription.validUntil
                                                  ).toLocaleDateString('pt-BR')
                                                : '-'}
                                        </td>
                                        <td>
                                            {new Date(
                                                prescription.createdAt
                                            ).toLocaleDateString('pt-BR')}
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() =>
                                                        setSelectedPrescription(
                                                            prescription
                                                        )
                                                    }
                                                    className="btn btn-ghost btn-sm"
                                                    title="Ver detalhes"
                                                >
                                                    <MdVisibility className="text-lg" />
                                                </button>
                                                {prescription.qrCode && (
                                                    <button
                                                        onClick={() =>
                                                            handleShowQrCode(
                                                                prescription.id
                                                            )
                                                        }
                                                        className="btn btn-ghost btn-sm"
                                                        title="QR Code"
                                                    >
                                                        <MdQrCode className="text-lg" />
                                                    </button>
                                                )}
                                                {(prescription.status ===
                                                    'PRESCRIPTION_CREATED' ||
                                                    prescription.status ===
                                                        'PRESCRIPTION_SIGNED') && (
                                                    <button
                                                        onClick={() =>
                                                            handleSend(
                                                                prescription.id
                                                            )
                                                        }
                                                        className="btn btn-ghost btn-sm text-secondary"
                                                        title="Enviar"
                                                        disabled={
                                                            sending ===
                                                            prescription.id
                                                        }
                                                    >
                                                        {sending ===
                                                        prescription.id ? (
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        ) : (
                                                            <MdSend className="text-lg" />
                                                        )}
                                                    </button>
                                                )}
                                                {prescription.status !==
                                                    'PRESCRIPTION_DISPENSED' &&
                                                    prescription.status !==
                                                        'PRESCRIPTION_CANCELLED' && (
                                                        <button
                                                            onClick={() =>
                                                                handleCancel(
                                                                    prescription.id
                                                                )
                                                            }
                                                            className="btn btn-ghost btn-sm text-error"
                                                            title="Cancelar"
                                                        >
                                                            <MdCancel className="text-lg" />
                                                        </button>
                                                    )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Prescription Detail Modal */}
            {selectedPrescription && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-3xl">
                        <h3 className="font-bold text-lg mb-4">
                            Detalhes da Receita
                        </h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Status
                                    </label>
                                    <p>
                                        <span
                                            className={`badge ${PRESCRIPTION_STATUS_COLORS[selectedPrescription.status]}`}
                                        >
                                            {
                                                PRESCRIPTION_STATUS_LABELS[
                                                    selectedPrescription.status
                                                ]
                                            }
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Código de Acesso
                                    </label>
                                    <p className="font-medium font-mono">
                                        {selectedPrescription.accessCode || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Integração
                                    </label>
                                    <p className="font-medium">
                                        {selectedPrescription.integration
                                            ?.name || '-'}
                                    </p>
                                </div>
                            </div>

                            <div className="divider"></div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Paciente
                                    </label>
                                    <p className="font-medium">
                                        {selectedPrescription.client?.name ||
                                            '-'}
                                    </p>
                                    {selectedPrescription.client?.cpf && (
                                        <p className="text-sm text-base-content/60">
                                            CPF:{' '}
                                            {selectedPrescription.client.cpf}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Profissional
                                    </label>
                                    <p className="font-medium">
                                        {selectedPrescription.professional
                                            ?.name || '-'}
                                    </p>
                                    {selectedPrescription.professional
                                        ?.registrationNumber && (
                                        <p className="text-sm text-base-content/60">
                                            {
                                                selectedPrescription
                                                    .professional.councilName
                                            }
                                            :{' '}
                                            {
                                                selectedPrescription
                                                    .professional
                                                    .registrationNumber
                                            }
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="divider"></div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Criada em
                                    </label>
                                    <p className="font-medium">
                                        {new Date(
                                            selectedPrescription.createdAt
                                        ).toLocaleString('pt-BR')}
                                    </p>
                                </div>
                                {selectedPrescription.signedAt && (
                                    <div>
                                        <label className="text-sm text-base-content/60">
                                            Assinada em
                                        </label>
                                        <p className="font-medium">
                                            {new Date(
                                                selectedPrescription.signedAt
                                            ).toLocaleString('pt-BR')}
                                        </p>
                                    </div>
                                )}
                                {selectedPrescription.sentAt && (
                                    <div>
                                        <label className="text-sm text-base-content/60">
                                            Enviada em
                                        </label>
                                        <p className="font-medium">
                                            {new Date(
                                                selectedPrescription.sentAt
                                            ).toLocaleString('pt-BR')}
                                        </p>
                                    </div>
                                )}
                                {selectedPrescription.dispensedAt && (
                                    <div>
                                        <label className="text-sm text-base-content/60">
                                            Dispensada em
                                        </label>
                                        <p className="font-medium">
                                            {new Date(
                                                selectedPrescription.dispensedAt
                                            ).toLocaleString('pt-BR')}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {selectedPrescription.prescriptionUrl && (
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Link da Receita
                                    </label>
                                    <a
                                        href={
                                            selectedPrescription.prescriptionUrl
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link link-primary block"
                                    >
                                        {selectedPrescription.prescriptionUrl}
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => setSelectedPrescription(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setSelectedPrescription(null)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}

            {/* QR Code Modal */}
            {qrCodeModal.open && (
                <dialog open className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4 text-center">
                            QR Code da Receita
                        </h3>
                        <div className="flex justify-center">
                            <img
                                src={qrCodeModal.qrCode}
                                alt="QR Code"
                                className="max-w-full"
                            />
                        </div>
                        <p className="text-sm text-base-content/60 text-center mt-4">
                            Escaneie este código na farmácia para dispensar a
                            receita
                        </p>
                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() =>
                                    setQrCodeModal({ open: false, qrCode: '' })
                                }
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button
                            onClick={() =>
                                setQrCodeModal({ open: false, qrCode: '' })
                            }
                        >
                            close
                        </button>
                    </form>
                </dialog>
            )}

            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false)
                    setPrescriptionToCancel(null)
                }}
                onConfirm={confirmCancel}
                title="Cancelar receita"
                message="Tem certeza de que deseja cancelar esta receita eletrônica?"
                confirmButtonText="Cancelar Receita"
                cancelButtonText="Voltar"
                loading={cancelling}
            />
        </div>
    )
}
